import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, PartyPopper, Zap, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isActive } from "@/App";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [waitedLong, setWaitedLong] = useState(false);
  const pollRef = useRef(null);

  const active = isActive(user);

  // Poll our own backend (not URL params) for subscription status.
  useEffect(() => {
    checkAuth();
    pollRef.current = setInterval(() => { checkAuth(); }, 4000);
    const t = setTimeout(() => setWaitedLong(true), 20000);
    return () => { clearInterval(pollRef.current); clearTimeout(t); };
  }, [checkAuth]);

  useEffect(() => {
    if (active) {
      if (pollRef.current) clearInterval(pollRef.current);
      const r = setTimeout(() => navigate("/dashboard"), 1200);
      return () => clearTimeout(r);
    }
  }, [active, navigate]);

  return (
    <div className="min-h-screen bg-sk-bg text-sk-cream flex flex-col">
      <header className="border-b border-sk-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <img src="/logo.png" alt="Studio Kreator" className="h-8 w-auto" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl border border-sk-line bg-sk-surface p-8 sm:p-10 shadow-sm text-center max-w-md w-full" data-testid="payment-success-card">
          {active ? (
            <>
              <span className="h-16 w-16 rounded-2xl bg-sk-brand/12 flex items-center justify-center mx-auto mb-5"><PartyPopper className="h-8 w-8 text-sk-brand" /></span>
              <h1 className="font-extrabold text-2xl sm:text-3xl tracking-tight text-sk-cream">Langganan aktif!</h1>
              <p className="text-sk-muted mt-3 leading-relaxed">Pembayaran kamu berhasil. Mengarahkan ke dashboard...</p>
              <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn" className="mt-7 w-full flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3.5 hover:bg-sk-brandDark transition-colors shadow-lg shadow-sk-brand/20">
                <Zap className="h-4 w-4" /> Buka Dashboard
              </button>
            </>
          ) : (
            <>
              <span className="h-16 w-16 rounded-2xl bg-sk-brand/12 flex items-center justify-center mx-auto mb-5"><Clock className="h-8 w-8 text-sk-brand animate-pulse" /></span>
              <h1 className="font-extrabold text-2xl tracking-tight text-sk-cream" data-testid="payment-waiting">Menunggu konfirmasi pembayaran...</h1>
              <p className="text-sk-muted mt-3 leading-relaxed">Kami sedang memverifikasi pembayaran kamu. Halaman ini akan otomatis lanjut begitu langganan aktif.</p>
              <p className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-sk-muted">
                <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sk-brand opacity-70" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sk-brand" /></span>
                Mengecek status otomatis
              </p>
              {waitedLong && (
                <button onClick={() => navigate("/checkout")} data-testid="back-checkout-btn" className="mt-6 w-full rounded-full border border-sk-line py-3 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                  Butuh waktu lebih lama dari biasanya — kembali ke checkout
                </button>
              )}
              <p className="text-[11px] text-sk-muted mt-4 flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Pembayaran diproses aman lewat Midtrans</p>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
